import { trackOnboardingV5StepSubmitted } from "@/features/onboarding/analytics";
import { onboardingRoutes } from "@/router/step-routes";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import { useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

export const useStepRouteInfo = () => {
  const location = useLocation();
  const navigation = useIonRouter();
  const pathname = location.pathname;
  const routeInfo = onboardingRoutes.find((route) => route.path === pathname);
  const steps = useMemo(() => {
    return onboardingRoutes.filter((route) => route.step !== undefined);
  }, []);

  // Prevents a second tap from firing a duplicate navigation while the first
  // transition is still playing. The ref (not state) avoids an extra render.
  const isNavigatingRef = useRef(false);

  // Pages stay mounted underneath the one that replaced them, so isNavigatingRef
  // persists across back/forward navigations. Reset it whenever the page transitions
  // back into view so the FAB works again.
  useIonViewWillEnter(() => {
    isNavigatingRef.current = false;
  });

  // No { unmount: true }: it was passed alongside routeDirection:"none", which made
  // Ionic ignore it. Now that the direction is real it would take effect for the first
  // time and rebuild each screen on the way back, losing the user's answers.
  const navigateToNextStep = useCallback(async () => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    const nextPath = routeInfo?.nextPath;
    trackOnboardingV5StepSubmitted(pathname);
    navigation.push(nextPath ?? "", "forward", "push", undefined, onboardingFadeTransition);
  }, [routeInfo, navigation, pathname]);

  const preloadNextRoute = useCallback(async () => {
    const nextPath = routeInfo?.nextPath;
    const nextRoute = onboardingRoutes.find((route) => route.path === nextPath);
    if (nextRoute?.lazyRoute) {
      nextRoute.lazyRoute.preload();
    }
  }, [routeInfo]);

  // Path of the step two hops ahead — used to skip a conditional step
  // (e.g. the "Why?" tracking step) that doesn't apply to the user's answer.
  const stepAfterNextPath = useMemo(() => {
    const nextRoute = onboardingRoutes.find((route) => route.path === routeInfo?.nextPath);
    return nextRoute?.nextPath;
  }, [routeInfo]);

  const navigateSkippingNextStep = useCallback(async () => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    trackOnboardingV5StepSubmitted(pathname);
    navigation.push(
      stepAfterNextPath ?? "",
      "forward",
      "push",
      undefined,
      onboardingFadeTransition,
    );
  }, [stepAfterNextPath, navigation, pathname]);

  const preloadRouteAfterNext = useCallback(async () => {
    const route = onboardingRoutes.find((r) => r.path === stepAfterNextPath);
    if (route?.lazyRoute) {
      route.lazyRoute.preload();
    }
  }, [stepAfterNextPath]);

  // Path of the step two hops back — used as the back target when the
  // immediately-previous step was skipped (e.g. the "Why?" tracking step).
  const stepBeforePreviousPath = useMemo(() => {
    const prevRoute = onboardingRoutes.find((route) => route.path === routeInfo?.previousPath);
    return prevRoute?.previousPath;
  }, [routeInfo]);

  const preloadAllRoutes = useCallback(async () => {
    await Promise.all(
      onboardingRoutes.map((route) => {
        if (route.lazyRoute) {
          return route.lazyRoute.preload();
        }
      }),
    );
  }, []);
  return {
    info: routeInfo,
    steps,
    totalSteps: steps.length,
    navigateToNextStep,
    navigateSkippingNextStep,
    stepBeforePreviousPath,
    preloadNextRoute,
    preloadRouteAfterNext,
    preloadAllRoutes,
  };
};
